import React, { useState, useEffect } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import maa from "../../Assets/Logos/logo.png";
import InquiryModal from "../UserSideComponents/Modal/InquiryModal";


const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { name: "Home",    path: "/" },
    { name: "About us",   path: "/about" },
    { name: "Cab packages",     path: "/car" },
    { name: "Tour Packages",    path: "/tour" },
    { name: "Contact us", path: "/contact" },
  ];

  const activeMenu =
    menuItems.find((item) => item.path === location.pathname)?.name || "Home";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMenuOpen]);

  const handleMenuClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // ─── Theming helpers ────────────────────────────────────────────────────────
  const bg        = isDark ? "bg-gray-900"   : "bg-white";
  const textMain  = isDark ? "text-gray-100" : "text-gray-700";
  const iconHover = isDark ? "hover:bg-gray-700" : "hover:bg-gray-100";
  const borderCol = isDark ? "border-gray-700" : "border-gray-200";
  const activeCol = "text-red-500";
  const activeBg  = isDark ? "bg-red-900/30" : "bg-red-50";

  return (
    <>
      {/* ─── NAVBAR ──────────────────────────────────────────────────────────── */}
      <nav
        className={`fixed left-0 w-full z-50 transition-all duration-500 ${
          isScrolled
            ? `top-0 ${bg} shadow-md`
            : `top-0 md:top-9 md:px-8 lg:px-20 bg-transparent`
        }`}
      >
        <div
          className={`mx-auto transition-all duration-500 ${
            isScrolled
              ? `w-full ${bg} px-4 md:px-8 lg:px-20`
              : `container ${isDark ? "bg-gray-900/95" : "bg-white/95"} lg:px-10 md:rounded-xl`
          }`}
        >
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => handleMenuClick("/")}
            >
              <div className="w-28 h-10 flex items-center justify-center">
                <img src={maa} alt="Logo" className="w-28 h-auto" />
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center">
              {menuItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleMenuClick(item.path)}
                  className={`flex items-center gap-1 px-4 py-2 text-[15px] font-medium transition-colors ${
                    activeMenu === item.name
                      ? activeCol
                      : `${textMain} hover:text-red-500`
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* Right — Inquiry Button (Desktop) */}
            <div className="hidden lg:flex items-center gap-2">
              <button
                onClick={() => setIsInquiryModalOpen(true)}
                className="ml-2 px-6 py-2.5 text-sm font-semibold rounded-lg transition-colors
                  bg-[#CC3316] text-white hover:bg-white hover:text-[#CC3316] border border-[#CC3316]  duration-300"
              >
                Inquiry Now
              </button>
            </div>

            {/* Mobile Hamburger */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg ${iconHover} transition-colors`}
              >
                {isMenuOpen
                  ? <X    className={`w-6 h-6 ${textMain}`} />
                  : <Menu className={`w-6 h-6 ${textMain}`} />
                }
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── MOBILE: Overlay backdrop ───────────────────────────────────────── */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* ─── MOBILE: Right-side slide drawer ────────────────────────────────── */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-72 z-50 shadow-2xl flex flex-col
          ${isDark ? "bg-gray-900" : "bg-white"}
          ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)" }}
      >
        {/* Drawer Header */}
        <div className={`flex items-center justify-between px-5 py-5 border-b ${borderCol}`}>
          <span className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
            Menu
          </span>
          <button
            onClick={() => setIsMenuOpen(false)}
            className={`p-2 rounded-lg ${iconHover} transition-colors`}
          >
            <X className={`w-5 h-5 ${textMain}`} />
          </button>
        </div>

        {/* Drawer Nav Items */}
        <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
          {menuItems.map((item, i) => (
            <button
              key={item.name}
              onClick={() => handleMenuClick(item.path)}
              style={{ animationDelay: `${i * 50}ms` }}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeMenu === item.name
                  ? `${activeCol} ${activeBg}`
                  : `${textMain} ${iconHover}`
              } ${isMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}
            >
              <span>{item.name}</span>
              <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
            </button>
          ))}
        </div>

        {/* Drawer Footer — Inquiry Button (Mobile) */}
        <div className={`px-4 py-5 border-t ${borderCol}`}>
          <button
            onClick={() => {
              setIsInquiryModalOpen(true);
              setIsMenuOpen(false);
            }}
            className={`w-full px-6 py-3 text-sm font-semibold rounded-lg transition-colors ${
              isDark
                ? "bg-white text-gray-900 hover:bg-gray-200"
                : "bg-red-600 text-white hover:bg-gray-800"
            }`}
          >
            Inquiry Now
          </button>
        </div>
      </div>

      {/* ─── INQUIRY MODAL (separated component) ────────────────────────────── */}
      <InquiryModal
        isOpen={isInquiryModalOpen}
        onClose={() => setIsInquiryModalOpen(false)}
        isDark={isDark}
      />
    </>
  );
};

export default Navbar;